//Built-in libraries

//Third party libraries
import { PlusIcon } from '@heroicons/react/24/outline';
import { type ChangeEvent, useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import InputError from '@/components/UI/Form/Error';
import Input from '@/components/UI/Form/Input';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { isObjectEmpty } from '@/utils';

//STP Libraries

import RadioGroupInput from '@/components/UI/Form/RadioGroup';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { PercentFormatDisplay } from '@/components/UI/NumberFormatDisplay/PercentFormatDisplay';
//Assets and Miscellaneous
import type { ReactSelectOption } from '@/types/Constants';
import { formatDate3 } from '@/utils';
import { debtTypes, initialValues } from './constants';
import type { IIndebtedness } from './types';

//Component Begin

const Indebtedness = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editDebtId, setEditDebtId] = useState<string>('');
  const [selectedDebt, setSelectedDebt] = useState<IIndebtedness>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [debt, setDebt] = useState(initialValues);

  const { updateData, data } = useApplicationFormContext();

  const resetForm = () => {
    setDebt(initialValues);
  };

  const editDebtHandler = (d: any) => {
    setIsEditing(true);
    const formData = Object.keys(d).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...debt[next],
          value: d[next],
          isTouched: !!d[next],
        },
      };
    }, {});
    setDebt(formData);
    setEditDebtId(d.id);
  };

  const updateDebtHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(debt).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: debt[next].value,
            id: editDebtId,
          };
        },
        {}
      );
      updateData({
        step2: {
          ...data.step2,
          section1: data.step2.section1.map((p: any) => {
            if (p.id === editDebtId) {
              return formattedData;
            }
            return p;
          }),
        },
      });
      resetForm();
      setIsEditing(false);
    }
  };

  const removeDebtHandler = (debt: IIndebtedness) => {
    setShowRemoveConfirm(true);
    setSelectedDebt(debt);
  };
  const confirmRemoveDebt = async () => {
    setShowRemoveConfirm(false);
    const filteredDebts = data.step2.section1.filter((debt: any) => debt.id !== selectedDebt?.id);
    updateData({
      step2: {
        ...data.step2,
        section1: filteredDebts,
      },
    });

    if (selectedDebt?.id === editDebtId) {
      resetForm();
      setIsEditing(false);
    }
  };

  const cancelRemoveDebt = () => {
    setShowRemoveConfirm(false);
  };

  const validateForm = () => {
    const formData = { ...debt };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setDebt((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddDebtHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(debt).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: debt[next].value };
        },
        {}
      );
      updateData({
        step2: {
          ...data.step2,
          section1: [...data.step2.section1, { ...formattedData, id: uuidv4() }],
        },
      });
      resetForm();
    }
  };

  const inputChangedHandler = createInputChangedHandler(setDebt, debt);

  const [hasIndeptedness, setHasIndeptedness] = useState('no');

  const onIndeptednessChange = (value: string) => {
    if (value === 'no') {
      updateData({
        step2: {
          ...data.step2,
          section1: [],
        },
      });
      resetForm();
    }
    setHasIndeptedness(value);
  };

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove debt'
          message={`You are about to remove ${selectedDebt?.debtType} debt. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveDebt}
          decline={cancelRemoveDebt}
        />
      )}

      <section className='space-y-4'>
        <RadioGroupInput
          label='Are there any debts, notes, or mortgages on the building?'
          value={hasIndeptedness}
          onChange={onIndeptednessChange}
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
        />
        {hasIndeptedness === 'yes' && (
          <>
            <div>
              <h3>Current Indebtedness</h3>
              <p className='block text-sm text-neutralColor-normal'>
                Furnish information on BUILDING-RELATED debts, notes, and mortgages payable.
              </p>
            </div>

            {data.step2?.section1.length > 0 && (
              <div className='w-full h-full mt-4 ring-1 ring-neutralColor-lighter rounded-xl overflow-hidden'>
                <table className='divide-y divide-gray-200 w-full'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Type</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>To whom Payable</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Original Amount</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Origination Date</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Present Balance</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Interest Rate</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Maturity Date</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Monthly Payment</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'></th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {data.step2.section1.map((debt: IIndebtedness) => (
                      <tr key={debt.id} className={`${debt.id === editDebtId ? 'bg-neutralColor-lighter' : ''}`}>
                        <td className='p-4'>{debt.debtType.label}</td>
                        <td className='p-4'>{debt.payableTo}</td>
                        <td className='p-4'>
                          <CurrencyFormatDisplay value={debt.originalAmount ?? 0} />
                        </td>
                        <td className='p-4'>{formatDate3(debt.originationDate)}</td>
                        <td className='p-4'>
                          <CurrencyFormatDisplay value={debt.presentBalance ?? 0} />
                        </td>
                        <td className='p-4'>
                          <PercentFormatDisplay value={debt.interestRate ?? 0} />
                        </td>
                        <td className='p-4'>{formatDate3(debt.maturityDate)}</td>
                        <td className='p-4'>
                          <CurrencyFormatDisplay value={debt.monthlyPayment ?? 0} />
                        </td>
                        <td className='p-4'>
                          <div className='flex justify-end items-center self-center gap-4'>
                            <div className='cursor-pointer hover:text-blue-500' onClick={() => editDebtHandler(debt)}>
                              Edit
                            </div>
                            <div className='cursor-pointer hover:text-tertiaryColor-normal' onClick={() => removeDebtHandler(debt)}>
                              Delete
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className='mt-4 w-full grid grid-cols-6 gap-6'>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <ReactSelect
                  label='Type'
                  id='type'
                  name='debtType'
                  placeholder='Select'
                  options={debtTypes.map((d) => ({
                    label: d,
                    value: d,
                  }))}
                  value={{
                    label: debt.debtType.value,
                    value: debt.debtType.value,
                  }}
                  error={debt?.debtType?.error}
                  onChange={(option) => {
                    inputChangedHandler((option as ReactSelectOption).value, 'debtType');
                  }}
                  onBlur={(_e) => {
                    inputChangedHandler(debt.debtType.value, 'debtType');
                  }}
                />
              </div>
              <Input
                customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
                label='To whom Payable'
                id='payableTo'
                name='payableTo'
                type='text'
                value={debt.payableTo.value}
                error={debt?.payableTo?.error}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
              />
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='originalAmount'>
                  Original Amount ($)
                </label>
                <NumberFormat
                  name='originalAmount'
                  id='originalAmount'
                  value={debt.originalAmount.value ?? ''}
                  className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                  thousandSeparator={true}
                  decimalScale={2}
                  valueIsNumericString
                  prefix={'$ '}
                  maxLength={21}
                  onValueChange={(target, s) => {
                    if (s.source !== 'prop') {
                      inputChangedHandler(target.floatValue, 'originalAmount');
                    }
                  }}
                  onBlur={(e: any) => {
                    inputChangedHandler(debt.originalAmount.value, e.target.name);
                  }}
                />
                {debt?.originalAmount?.error && <InputError>{debt?.originalAmount?.error}</InputError>}
              </div>
              <Input
                customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
                label='Origination Date'
                id='originationDate'
                name='originationDate'
                type='date'
                value={debt.originationDate.value}
                error={debt?.originationDate?.error}
                onChange={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
              />
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='presentBalance'>
                  Present Balance ($)
                </label>
                <NumberFormat
                  name='presentBalance'
                  id='presentBalance'
                  value={debt.presentBalance.value ?? ''}
                  className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                  thousandSeparator={true}
                  decimalScale={2}
                  valueIsNumericString
                  prefix={'$ '}
                  maxLength={21}
                  onValueChange={(target, s) => {
                    if (s.source !== 'prop') {
                      inputChangedHandler(target.floatValue, 'presentBalance');
                    }
                  }}
                  onBlur={(e: any) => {
                    inputChangedHandler(debt.presentBalance.value, e.target.name);
                  }}
                />
                {debt?.presentBalance?.error && <InputError>{debt?.presentBalance?.error}</InputError>}
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='interestRate'>
                  Interest Rate (%)
                </label>
                <NumberFormat
                  name='interestRate'
                  id='interestRate'
                  value={debt.interestRate.value ?? ''}
                  className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                  valueIsNumericString
                  maxLength={3}
                  onValueChange={(target, s) => {
                    if (s.source !== 'prop') {
                      inputChangedHandler(target.floatValue, 'interestRate');
                    }
                  }}
                  onBlur={(e: any) => {
                    inputChangedHandler(debt.interestRate.value, e.target.name);
                  }}
                />
                {debt?.interestRate?.error && <InputError>{debt?.interestRate?.error}</InputError>}
              </div>
              <Input
                customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
                label='Maturity Date'
                id='maturityDate'
                name='maturityDate'
                type='date'
                value={debt.maturityDate.value}
                error={debt?.maturityDate?.error}
                onChange={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
              />
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='monthlyPayment'>
                  Monthly Payment ($)
                </label>
                <NumberFormat
                  name='monthlyPayment'
                  id='monthlyPayment'
                  value={debt.monthlyPayment.value ?? ''}
                  className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                  thousandSeparator={true}
                  decimalScale={2}
                  prefix={'$ '}
                  valueIsNumericString
                  maxLength={21}
                  onValueChange={(target, s) => {
                    if (s.source !== 'prop') {
                      inputChangedHandler(target.floatValue, 'monthlyPayment');
                    }
                  }}
                  onBlur={(e: any) => {
                    inputChangedHandler(debt.monthlyPayment.value, e.target.name);
                  }}
                />
                {debt?.monthlyPayment?.error && <InputError>{debt?.monthlyPayment?.error}</InputError>}
              </div>
            </div>
            {!isEditing && <Button icon={<PlusIcon />} label='Add' type='button' btnStyle='primary' onClick={onAddDebtHandler} />}
            {isEditing && (
              <div className='flex gap-4'>
                <Button label='Save' type='button' btnStyle='primary' onClick={updateDebtHandler} />
                <Button
                  label='Cancel'
                  type='button'
                  btnStyle='neutral'
                  onClick={() => {
                    resetForm();
                    setIsEditing(false);
                    setEditDebtId('');
                  }}
                />
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default Indebtedness;
